/**
 * Thin, defensive wrapper around `localStorage`.
 *
 * Every access is guarded: Safari in private mode throws on `setItem`, some
 * browsers throw merely on *reading* `window.localStorage`, and stored JSON may
 * have been written by an older version of the site. A storage failure must
 * never break the page, so all failures degrade to the supplied fallback.
 */

export const STORAGE_KEYS = {
  theme: 'athar.theme',
  gallery: 'athar.gallery',
  questions: 'athar.questions',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * Reach the store without touching it.
 *
 * Reads and writes are guarded independently on purpose: a storage that is full
 * still serves `getItem` perfectly well, so probing it with a throwaway
 * `setItem` would make every read fail and wipe the visitor's content out of
 * the UI the next time they load the page.
 */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read and validate a persisted value.
 *
 * @param parse Narrowing validator. Return `null` to reject malformed data and
 *              fall back — this is what protects the UI from stale schemas.
 */
export function readStored<T>(key: StorageKey, parse: (value: unknown) => T | null): T | null {
  const store = storage();
  if (!store) return null;

  try {
    const raw = store.getItem(key);
    if (raw === null) return null;
    return parse(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

/** Persist a value. Returns `false` when storage is unavailable or full. */
export function writeStored(key: StorageKey, value: unknown): boolean {
  const store = storage();
  if (!store) return false;

  try {
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removeStored(key: StorageKey): void {
  const store = storage();
  if (!store) return;

  try {
    store.removeItem(key);
  } catch {
    /* nothing useful to do */
  }
}
